// @ts-nocheck

import React, { useCallback, useMemo, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import SpinePlayer from '../../../../components/SpinePlayer';
import { SpineAnimationIds } from '../../../../types/animations';
import { SoundIds } from '../../../../types/sounds';
import { KLRoundViewByOrder } from '../../constants/order';
import { type KLRound, KLRounds } from '../../types/round';

import KLViewWrapper from './KLViewWrapper';

const ANIMATION_CHANGE_ROUND = 1370; // ms
const BG_ANIMATION_DURATION = 300; // ms
const BG_BRIGHTNESS = 0.4;

const brightnessOffAnimation = keyframes`
  from { filter: brightness(${BG_BRIGHTNESS}); }
  to { filter: brightness(0); }
`;

const brightnessOnAnimation = keyframes`
  from { filter: brightness(0); }
  to { filter: brightness(${BG_BRIGHTNESS}); }
`;

const StyledKLViewWrapper = styled(KLViewWrapper)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 0;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &:before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;

    background: inherit;
    filter: brightness(${BG_BRIGHTNESS});

    ${({ $startAnimation, $showNextRound }) =>
      $startAnimation &&
      (!$showNextRound
        ? css`
            animation: ${brightnessOffAnimation} ${BG_ANIMATION_DURATION}ms ease
              ${ANIMATION_CHANGE_ROUND - BG_ANIMATION_DURATION}ms;
          `
        : css`
            animation: ${brightnessOnAnimation} ${BG_ANIMATION_DURATION}ms ease;
          `)};

    animation-fill-mode: forwards;
  }
`;

const StyledSpinePlayer = styled(SpinePlayer)`
  width: 100%;
  height: 100%;
`;

const fadeInAnimation = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Text = styled.div`
  font-size: 1.1em;
  text-align: center;
  text-transform: uppercase;
  padding: 0 2em;

  opacity: 0;
  animation: ${fadeInAnimation} 1s ease 0.7s;
  animation-fill-mode: forwards;
`;

const Title = styled.div`
  font-size: 2em;
  font-weight: bold;

  padding-bottom: 0.5em;
`;

type KLNotificationRoundCompleteProps = {
  roundId: KLRound;
};

const KLNotificationRoundComplete = ({ roundId }: KLNotificationRoundCompleteProps) => {
  const [viewType, setViewType] = useState(roundId);
  const [showNextRound, setShowNextRound] = useState(false);
  const [hasAnimationStarted, setHasAnimationStarted] = useState(false);

  const nextRound = useMemo(() => {
    const nextRoundIndex = KLRoundViewByOrder.indexOf(roundId) + 1;
    return KLRoundViewByOrder[nextRoundIndex];
  }, [roundId]);

  const onStartAnimation = useCallback(() => {
    setShowNextRound(false);
    setHasAnimationStarted(true);

    setTimeout(() => {
      setViewType(nextRound);
      setShowNextRound(true);
    }, ANIMATION_CHANGE_ROUND);
  }, [nextRound]);

  const onEndAnimation = useCallback(() => {
    setHasAnimationStarted(false);
  }, []);

  const content = useMemo(() => {
    switch (roundId) {
      case KLRounds.T_RED:
        return { title: 'Réunification !', text: 'Vous appartenez désormais à la tribu blanche.' };
      case KLRounds.T_WHITE:
        return { title: undefined, text: `C'est l'heure de la célèbre course d'orientation !` };
      case KLRounds.ORIENTATION:
        return { title: undefined, text: `Place à l'épreuve mythique des poteaux` };
      default:
        return {};
    }
  }, [roundId]);

  const soundId = useMemo(() => {
    switch (roundId) {
      case KLRounds.T_RED:
        return SoundIds.KL_TRANSITION_T_WHITE;
      case KLRounds.T_WHITE:
        return SoundIds.KL_TRANSITION_ORIENTATION;
      case KLRounds.ORIENTATION:
        return SoundIds.KL_TRANSITION_POLE;
      default:
        return;
    }
  }, [roundId]);

  return (
    <StyledKLViewWrapper
      viewType={viewType}
      preloadViewType={nextRound}
      $showNextRound={showNextRound}
      $startAnimation={hasAnimationStarted}
    >
      <StyledSpinePlayer
        animationId={SpineAnimationIds.KL_ROUND_COMPLETE}
        soundId={soundId}
        onStartAnimation={onStartAnimation}
        onEndAnimation={onEndAnimation}
      />
      {showNextRound ? (
        <Text>
          <Title>{content.title}</Title>
          {content.text}
        </Text>
      ) : undefined}
    </StyledKLViewWrapper>
  );
};

export default KLNotificationRoundComplete;
